import { AppRoute, QueryReference } from '@agentselly/immodossier-shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { formatDateTime } from '@agentselly/ui';
import { clsx } from 'clsx';
import { useState } from 'react';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { LegacyEvaluationsPage } from '~/components/admin/LegacyEvaluationsPage';
import { V3Dialog } from '~/components/admin/V3Dialog';
import {
  V3DocIcon,
  V3EmptyBlock,
  V3ErrorBlock,
  V3Skeleton,
} from '~/components/admin/V3EmptyBlock';
import { useAppParams } from '~/helper/useAppParams';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_ADMIN_EVALUATIONS,
)({
  component: Body,
});

function Body() {
  const isV3 = useV3Redesign();
  const search = Route.useSearch();

  if (!isV3) {
    return (
      <LegacyEvaluationsPage
        search={search}
        fullPath={Route.fullPath}
      />
    );
  }

  return <V3Evaluations />;
}

const SELL: Record<string, [string, string]> = {
  sell12: [
    'SQL',
    'c-ok',
  ],
  sell24: [
    'MQL',
    'c-info',
  ],
  sell: [
    'Unklar',
    'c-neu',
  ],
  buy: [
    'Kauf',
    'c-neu',
  ],
  value: [
    'Wert',
    'c-neu',
  ],
};

function V3Evaluations() {
  const navigate = useNavigate();
  const { lang, companyId } = useAppParams();
  const currentFilter: QueryFilter = Route.useSearch();
  const query = useBackendQuery(QueryReference.PageAdminEvaluations);
  const [
    raw,
    setRaw,
  ] = useState<{ id: string; title: string; value: unknown } | null>(null);

  async function updateFilter(filter: QueryFilter) {
    const nextSearch: QueryFilter = {};

    for (const key of [
      'page',
      'showFollowup',
      'showInternal',
      'hideErrors',
      'hideSuccess',
      'wannaSell',
      'appId',
      'showOnlyValiant',
    ]) {
      const value = key in filter ? filter[key] : currentFilter[key];

      if (typeof value === 'undefined' || !value.length) {
        continue;
      }

      nextSearch[key] = value;
    }

    await navigate({
      to: Route.fullPath,
      params: { lang, companyId },
      search: nextSearch as unknown as QueryFilter,
      replace: true,
    });

    await query.refetch();
  }

  const head = (
    <div
      className="v3-head"
      data-od-id="page-head"
    >
      <h1>Bewertungen</h1>
      <p className="v3-mu">Alle Eingaben aus dem Rechner, neueste zuerst.</p>
    </div>
  );

  if (query.isLoading) {
    return (
      <div
        className="v3-stack pb-8"
        aria-busy="true"
      >
        {head}
        <V3Skeleton />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="v3-stack pb-8">
        {head}
        <V3ErrorBlock
          title="Bewertungen konnten nicht geladen werden"
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const evaluations = (query.data?.evaluations ?? []).filter(Boolean);
  const pagination = query.data?.pagination;
  const page = pagination?.page || 1;
  const pageCount = pagination?.pageCount || 1;
  const stand = query.data?.resultTimestamp
    ? new Date(query.data.resultTimestamp).toLocaleTimeString('de-CH', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '–';

  const ck = (id: string, label: string, checked: boolean, next: string) => (
    <label
      className="v3-ckb"
      htmlFor={id}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) =>
          updateFilter({ [id]: e.target.checked ? next : '', page: '' })
        }
      />
      <span>{label}</span>
    </label>
  );

  return (
    <div className="v3-stack pb-8">
      {head}

      <section
        className="v3-card"
        data-od-id="filters"
        aria-label="Filter"
      >
        <div className="v3-fil">
          <label className="f">
            Verkaufsabsicht
            <select
              className="v3-sel"
              value={currentFilter.wannaSell || ''}
              onChange={(e) =>
                updateFilter({ wannaSell: e.target.value, page: '' })
              }
            >
              <option value="">Alle</option>
              <option value="sell12">SQL (12 Monate)</option>
              <option value="sell24">MQL (24 Monate)</option>
              <option value="sell">Unklar</option>
              <option value="buy">Kauf</option>
              <option value="value">Wert</option>
            </select>
          </label>
          <label className="f">
            App
            <select
              className="v3-sel"
              value={currentFilter.appId || ''}
              onChange={(e) =>
                updateFilter({ appId: e.target.value, page: '' })
              }
            >
              <option value="">Alle</option>
              <option value="en6fcr">en6fcr</option>
              <option value="dca7b5">dca7b5</option>
            </select>
          </label>
          <div>
            {ck(
              'showInternal',
              'Interne anzeigen',
              Boolean(currentFilter.showInternal),
              'true',
            )}
            {ck(
              'showOnlyValiant',
              'Nur Valiant',
              Boolean(currentFilter.showOnlyValiant),
              'true',
            )}
          </div>
          <div>
            {ck(
              'hideErrors',
              'Fehler ausblenden',
              Boolean(currentFilter.hideErrors),
              'true',
            )}
            {ck(
              'hideSuccess',
              'Erfolge ausblenden',
              Boolean(currentFilter.hideSuccess),
              'true',
            )}
          </div>
        </div>
      </section>

      {evaluations.length ? (
        <div
          className="v3-stack"
          data-od-id="eval-list"
        >
          {evaluations.map((ev, index) => {
            const id = ev.sessionId || String(index);
            const isOk = ev.isSuccessful && !ev.hasError;
            const isBad = ev.hasError && !ev.isSuccessful;
            const sell = ev.wannaSell ? SELL[ev.wannaSell] : undefined;
            const links: Array<[string, string | undefined]> = [
              [
                'Ergebnisseite',
                ev.links?.resultpage,
              ],
              [
                'HubSpot-Objekt',
                ev.links?.hubspotProperty,
              ],
              [
                'HubSpot-Kontakt',
                ev.links?.hubspotContact,
              ],
            ];
            const rawData = {
              sessionId: ev.sessionId,
              appId: ev.appId,
              calculatorVersion: ev.rawCalculatorResult?.calculatorVersion,
              utm: ev.rawUTM,
              calculatorInput: ev.rawCalculatorInput,
              calculatorResult: ev.rawCalculatorResult,
              dossier: ev.rawDossier,
            };
            const utm = [
              ev.utmSource,
              ev.utmMedium,
              ev.utmCampaign,
            ]
              .filter(Boolean)
              .join(' / ');

            return (
              <article
                key={id + index}
                className={clsx(
                  'v3-card v3-ev',
                  isOk ? 's-ok' : isBad ? 's-bad' : 's-warn',
                  ev.valiantDeviceId && 's-val',
                )}
                data-od-id={`eval-${id}`}
                aria-labelledby={`t-${index}`}
              >
                <div className="v3-ev-top">
                  <div>
                    <p
                      className="v3-ev-title"
                      id={`t-${index}`}
                    >
                      {ev.address || 'Ohne Adresse'}
                    </p>
                    <p className="v3-xs v3-mu v3-num">
                      {formatDateTime(ev.timestamp)}
                    </p>
                  </div>
                  <div
                    className="flex flex-wrap"
                    style={{ gap: 6 }}
                  >
                    <span
                      className={clsx(
                        'v3-chip',
                        isOk ? 'c-ok' : isBad ? 'c-bad' : 'c-warn',
                      )}
                    >
                      <i />
                      {isOk ? 'Erfolgreich' : isBad ? 'Fehler' : 'Teilweise'}
                    </span>
                    {sell ? (
                      <span className={`v3-chip ${sell[1]}`}>
                        <i />
                        {sell[0]}
                      </span>
                    ) : null}
                    {ev.valiantDeviceId ? (
                      <span className="v3-chip c-info">
                        <i />
                        Valiant
                      </span>
                    ) : null}
                    {ev.isInternal ? (
                      <span className="v3-chip c-neu">
                        <i />
                        Intern
                      </span>
                    ) : null}
                  </div>
                </div>

                {ev.errorType ? (
                  <div
                    className="v3-warnbox"
                    role="alert"
                  >
                    <b>{ev.errorType}</b>
                    {ev.errorData?.source ? ` · ${ev.errorData.source}` : ''}
                    {ev.errorData?.message ? (
                      <>
                        <br />
                        {ev.errorData.message}
                      </>
                    ) : null}
                    {ev.errorData?.stack ? (
                      <details className="v3-xs">
                        <summary>Stack Trace anzeigen</summary>
                        <pre className="v3-pre">{ev.errorData.stack}</pre>
                      </details>
                    ) : null}
                  </div>
                ) : null}

                <dl className="v3-kv">
                  <div>
                    <dt>Kontakt</dt>
                    <dd>
                      {ev.contactName || <span className="v3-mu">Anonym</span>}
                    </dd>
                  </div>
                  <div>
                    <dt>E-Mail</dt>
                    <dd>{ev.contactEmail || '–'}</dd>
                  </div>
                  <div>
                    <dt>IAZI-Schätzung</dt>
                    <dd className="v3-num">
                      {ev.iaziEvaluation
                        ? `CHF ${Number(ev.iaziEvaluation).toLocaleString(
                            'de-CH',
                          )}`
                        : '–'}
                    </dd>
                  </div>
                  <div>
                    <dt>Trust-Score</dt>
                    <dd className="v3-num">
                      {ev.trustScore == null ? '–' : `${ev.trustScore} / 100`}
                    </dd>
                  </div>
                  <div>
                    <dt>Quelle (UTM)</dt>
                    <dd>{utm || '–'}</dd>
                  </div>
                  <div>
                    <dt>App</dt>
                    <dd className="v3-num">
                      {ev.appId || '–'}
                      {ev.rawCalculatorResult?.calculatorVersion
                        ? ` · v${ev.rawCalculatorResult.calculatorVersion}`
                        : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Session</dt>
                    <dd className="v3-num">{ev.sessionId || '–'}</dd>
                  </div>
                </dl>

                <div className="v3-lk">
                  {links
                    .filter(
                      ([
                        ,
                        href,
                      ]) => Boolean(href),
                    )
                    .map(
                      ([
                        label,
                        href,
                      ]) => (
                        <a
                          key={label}
                          className="v3-btn sm"
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ),
                    )}
                  <button
                    type="button"
                    className="v3-btn sm"
                    onClick={() =>
                      setRaw({
                        id: `raw-${id}`,
                        title: 'Rohdaten',
                        value: rawData,
                      })
                    }
                  >
                    Rohdaten
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <V3EmptyBlock
          icon={V3DocIcon}
          title="Keine Bewertungen gefunden"
          text="Mit diesen Filtern gibt es keine Treffer. Filter lockern oder zurücksetzen."
          actions={
            <button
              type="button"
              className="v3-btn"
              onClick={() =>
                navigate({
                  to: Route.fullPath,
                  params: { lang, companyId },
                  search: {} as unknown as QueryFilter,
                  replace: true,
                }).then(() => query.refetch())
              }
            >
              Filter zurücksetzen
            </button>
          }
        />
      )}

      {evaluations.length ? (
        <nav
          className="v3-pg"
          aria-label="Seiten"
          data-od-id="pagination"
        >
          <span className="v3-xs v3-mu v3-num">
            {pagination?.total ?? evaluations.length} Treffer · Stand {stand}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="v3-btn"
              disabled={page <= 1}
              onClick={() => updateFilter({ page: String(page - 1) })}
            >
              Zurück
            </button>
            <button
              type="button"
              className="v3-btn"
              disabled={page >= pageCount}
              onClick={() => updateFilter({ page: String(page + 1) })}
            >
              Weiter
            </button>
          </span>
        </nav>
      ) : null}

      {raw ? (
        <V3Dialog
          id={raw.id}
          title={raw.title}
          onClose={() => setRaw(null)}
        >
          <pre className="v3-pre">{JSON.stringify(raw.value, null, 2)}</pre>
        </V3Dialog>
      ) : null}
    </div>
  );
}

interface QueryFilter extends Record<string, string | string[] | undefined> {
  readonly page?: string;
  readonly showFollowup?: string;
  readonly showInternal?: string;
  readonly hideErrors?: string;
  readonly hideSuccess?: string;
  readonly wannaSell?: string;
  readonly appId?: string;
  readonly showOnlyValiant?: string;
}
